import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, } from "@mui/material";
import {
  StyledDarkRedInformationTypography,
  StyledInformationSpan,
  StyledMainBox,
} from "../../../components/commonComponents/StyledComponents/styled";
import { ArrowBack, ArrowForward, Close, Save, } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton, { DarkGreyButton } from "../../../components/commonComponents/Buttons/ActionButton";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import {
  checkUserDetailsFromCookie,
  fetchBrands,
} from "../../../api_calls/userManagement";
import { formatToStandardDate, standardFormatToday } from "../../../api_calls/utils";
import WizardStepper from "../../../components/commonComponents/Progress/WizardStepper";
import GeneralInfo from "./WizardSteps/GeneralInfo";
import Visuals from "./WizardSteps/Visuals";
import { useFileStore } from "./WizardSteps/FileProvider";
import StoresList from "./WizardSteps/StoresList";
// loop_prioritization: fetchVisualLimit disabled for pre_prod deploy — inStore not ready
// import { fetchVisualLimit, resetLoopsData, saveLoopCampaign, setLoopCampaignInfo, validateLoopCampaignGeneralInfo, validateStoreSelection, validateVisuals } from "../../../api_calls/loopCampaign";
import { fetchVisualLimit, resetLoopsData, saveLoopCampaign, setLoopCampaignInfo, validateLoopCampaignGeneralInfo, validateStoreSelection, validateVisuals } from "../../../api_calls/loopCampaign";
import Review from "./WizardSteps/Review";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { OWNER_SELECTABLE_USER_TYPES } from "../../../components/commonComponents/Selectors/OwnerField";
import { canEditAcrossCompanies } from "../../../api_calls/crossCompanyAccess";
import { getCreateFormOwnerDefault } from "../../../components/commonComponents/Selectors/companyHierarchySelectUtils";

const WIZARD_DIALOG_MAX_HEIGHT = 850;
const WIZARD_VIEWPORT_MARGIN = 24;
const wizardDialogPaperHeight = `min(${WIZARD_DIALOG_MAX_HEIGHT}px, calc(100dvh - ${WIZARD_VIEWPORT_MARGIN * 2}px))`;

const AddLoopCampaign = ({ open, onClose, size, usedFor, closeModal, loopinfo, cloneSource }) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [showCancelConfirmation, setShowCancelConfirmationConfirmation] = React.useState(false);
  const wizardRef = React.useRef(null);

  const statusBasedReadOnly =
    typeof currentStateData.new_pic_data !== "undefined" &&
      typeof currentStateData.new_pic_data.pic_start_end_status !== "undefined" &&
      ["Completed"].includes(currentStateData.new_pic_data.pic_start_end_status)
      ? true
      : false;

  const checkUserDetails = checkUserDetailsFromCookie();
  let userDetailObj = {};
  if (checkUserDetails.success === true) {
    userDetailObj = checkUserDetails.user_detail;
  }

  // -------- new loop code -------- //
  const { acceptedFiles } = useFileStore();
  const [activeStep, setActiveStep] = React.useState(0);

  const stepLabels = [
    "General Information & Schedule",
    "Visuals",
    "Stores",
    "Review",
  ];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            {/* Step 1: General Information & Schedule */}
            <GeneralInfo usedFor={usedFor} closeModal={closeModal} loopinfo={loopinfo} fieldErrors={fieldErrors} />
          </>
        );
      case 1:
        return (
          <>
            {/* Step 2: Visuals */}
            <Visuals usedFor={usedFor} loopInfo={loopinfo} />
          </>
        );
      case 2:
        return (
          <>
            {/* Step 3: Stores */}
            <StoresList usedFor={usedFor} closeModal={onClose} loopinfo={loopinfo} wizardRef={wizardRef} />
          </>
        );
      case 3:
        return (
          <>
            {/* Step 4: Review */}
            <Review loopInfo={loopinfo} usedFor={usedFor} />
          </>
        );
      default:
        return <></>;
    }
  };

  const dummyValidate = () => {
    return {
      error: false
    }
  }

  // validate the current step before proceeding
  const validateCurrentStep = () => {
    const validateFunctions = {
      0: () => validateLoopCampaignGeneralInfo(usedFor),
      1: () => validateVisuals(acceptedFiles, usedFor),
      2: () => validateStoreSelection(),
      3: () => dummyValidate()
    }

    const validationFunction = validateFunctions[activeStep];
    const validationResult = validationFunction();

    if (validationResult.error === true) {
      setFieldErrors(validationResult.errorMessages);
      return {
        error_found: true
      }
    } else {
      return {
        error_found: false
      }
    }
  }

  const updateLoopData = (loopData) => {
    const selectedLoopData = typeof currentStateData.new_loop_data !== "undefined" ? currentStateData.new_loop_data : {};
    dispatchAction(
      updateStateData({
        new_loop_data: { ...selectedLoopData, ...loopData },
      })
    );
  };

  //=============================================================================================

  React.useEffect(() => {
    // Update hydrates from the existing loop. Clone pre-fills a new loop from cloneSource.
    const isCloneMode = usedFor === "add" && typeof cloneSource !== "undefined" && cloneSource !== null && Object.keys(cloneSource).length !== 0;
    if (typeof loopinfo !== "undefined" && usedFor === "update") {
      setLoopCampaignInfo(loopinfo);
    } else if (isCloneMode) {
      // pre-fill from source loop campaign
      // The loop listing response carries no visual limits, so they are taken from
      // the same sources the add flow uses: the owner list for users who can edit
      // across companies, and the logged in user's own company otherwise.
      const cloneOwnerLimits =
        canEditAcrossCompanies(userDetailObj.user_type) === true
          ? typeof currentStateData.independent_owners_list !== "undefined"
            ? currentStateData.independent_owners_list.find((owner) => owner.id === cloneSource.company.id)
            : undefined
          : {
            visual_upload_limit: userDetailObj.loop_visual_limit,
            visual_upload_duration: userDetailObj.loop_visual_max_duration,
          };

      // loop_name omitted: backend may enforce unique campaign name per owner
      let cloneData = {
        loop_owner_id: cloneSource.company.id,
        loop_owner: cloneSource.company.name,
        loop_brand_company: cloneSource.advertiser.name,
        loop_brand_company_id: cloneSource.advertiser.id,
        brand_name: cloneSource.brand_name || "",
        manual_brand_company: false,
        visual_upload_limit: cloneOwnerLimits?.visual_upload_limit,
        visual_upload_duration: cloneOwnerLimits?.visual_upload_duration,
        share_list: [],
        share_list_selection: [],
        selected_stores: [],
      };

      // Today is a valid start/end input. Copy any source date that is today or later
      // instead of gating on status (start = today is Active, which previously skipped start).
      const today = standardFormatToday();
      const sourceStart = formatToStandardDate(cloneSource.start_date);
      const sourceEnd = formatToStandardDate(cloneSource.end_date);
      if (sourceStart >= today) {
        cloneData.loop_start_date = sourceStart;
      }
      if (sourceEnd >= today) {
        cloneData.loop_end_date = sourceEnd;
      }

      // Pre-fill Type from the source when it is known. If loop_campaign_types has
      // already loaded, only copy the type when it is available for this user.
      // When the map is not loaded yet (Type feature still flagged off, or the
      // visual-limit request has not returned), keep the source type; the effect
      // below clears it if the map later says it is not available.
      const sourceCampaignTypeId = cloneSource.loop_campaign_type_id;
      const typesMap = currentStateData.loop_campaign_types;
      const sourceTypeKnown = typeof sourceCampaignTypeId === "string" && sourceCampaignTypeId !== "";
      const sourceTypeAvailable =
        sourceTypeKnown &&
        (typeof typesMap === "undefined" || typesMap === null || typesMap[sourceCampaignTypeId] === true);
      if (sourceTypeAvailable) {
        cloneData.loop_campaign_type_id = sourceCampaignTypeId;
        cloneData.loop_campaign_type =
          sourceCampaignTypeId.charAt(0).toUpperCase() + sourceCampaignTypeId.slice(1).toLowerCase();
      }

      // store source stores for later pre-selection after capacity check
      if (cloneSource.store_list && cloneSource.store_list.length > 0) {
        cloneData.clone_source_stores = cloneSource.store_list.map(store => ({
          number_store_cyreen: store.number_store_cyreen,
          tree_path: store.tree_path,
        }));
      }

      updateLoopData(cloneData);
      fetchBrands(cloneSource.company.id);
      // loop_prioritization: disabled for pre_prod deploy — inStore not ready
      fetchVisualLimit(cloneSource.company.id);
    } else {
      const hierarchyRetailers =
        typeof currentStateData.company_hierarchy !== "undefined" &&
        Array.isArray(currentStateData.company_hierarchy.retailers)
          ? currentStateData.company_hierarchy.retailers
          : [];
      // Cyreen/tenant create: hierarchy root when the Owner picker is visible; else assigned company.
      if (OWNER_SELECTABLE_USER_TYPES.includes(userDetailObj.user_type) && usedFor !== "update") {
        const data = getCreateFormOwnerDefault(userDetailObj, {
          companyById: currentStateData.company_hierarchy_by_id,
          hierarchyRoot: currentStateData.company_hierarchy?.root,
          retailers: hierarchyRetailers,
          userType: userDetailObj.user_type,
          usedFor,
        });
        if (data !== undefined) {
          updateLoopData({
            loop_owner: data.label,
            loop_owner_id: data.id,
            loop_owner_company_level: data.company_level,
            share_list: [],
            share_list_selection: [],
          });
          fetchBrands(data.id);
          // loop_prioritization: disabled for pre_prod deploy — inStore not ready
          fetchVisualLimit(data.id);
        }
      }

      // Other user types: no owner UI — use assigned company for brands / visual limits / save.
      if (!OWNER_SELECTABLE_USER_TYPES.includes(userDetailObj.user_type) && usedFor !== "update") {
        fetchBrands();
        // loop_prioritization: disabled for pre_prod deploy — inStore not ready
        fetchVisualLimit(userDetailObj.assigned_company.id);
        updateLoopData({
          visual_upload_limit: userDetailObj.loop_visual_limit,
          visual_upload_duration: userDetailObj.loop_visual_max_duration
        })
      }
    }
  }, []);

  // Clone Type copy: when loop_campaign_types loads (qa/dev: fetchVisualLimit), keep
  // the source type only if it is available for this user. Also sync visual_upload_limit
  // to that type — fetchVisualLimit called without a type would otherwise leave the
  // commercial count on an unlimited copy. Do not fall back to a different type.
  React.useEffect(() => {
    const isCloneMode = usedFor === "add" && typeof cloneSource !== "undefined" && cloneSource !== null && Object.keys(cloneSource).length !== 0;
    if (!isCloneMode) return;

    const typesMap = currentStateData.loop_campaign_types;
    if (typeof typesMap === "undefined" || typesMap === null) return;

    const srcTypeId = cloneSource.loop_campaign_type_id;
    const typeIsAvailable = typeof srcTypeId === "string" && srcTypeId !== "" && typesMap[srcTypeId] === true;
    const currentTypeId = currentStateData.new_loop_data?.loop_campaign_type_id;
    const nextLimit = srcTypeId === "unlimited" ? typesMap.unlimited_limit_count : typesMap.commercial_limit_count;

    if (typeIsAvailable) {
      const nextLabel = srcTypeId.charAt(0).toUpperCase() + srcTypeId.slice(1).toLowerCase();
      const limitNeedsSync = typeof nextLimit !== "undefined" && currentStateData.new_loop_data?.visual_upload_limit !== nextLimit;
      if (currentTypeId !== srcTypeId || limitNeedsSync) {
        updateLoopData({
          loop_campaign_type_id: srcTypeId,
          loop_campaign_type: nextLabel,
          ...(typeof nextLimit !== "undefined" ? { visual_upload_limit: nextLimit } : {}),
        });
      }
    } else if (typeof currentTypeId !== "undefined" && currentTypeId !== "") {
      updateLoopData({
        loop_campaign_type_id: undefined,
        loop_campaign_type: undefined,
      });
    }
  }, [currentStateData.loop_campaign_types]);

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => {
          e.preventDefault();
        }}
        fullWidth={true}
        maxWidth="xl"
        sx={{
          "& .MuiDialog-paper": {
            height: wizardDialogPaperHeight,
            maxHeight: wizardDialogPaperHeight,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle> {usedFor === "update" ? "Edit : " + loopinfo?.name : "Add New Loop-based Campaign"} </DialogTitle>
        <DialogContent
          sx={{ flex: 1, minHeight: 0, overflowY: "auto", scrollbarGutter: "stable" }}
          ref={wizardRef}
        >

          <StyledMainBox component="main" sx={{ padding: 0 }}>
            <WizardStepper
              activeStep={activeStep}
              stepLabels={stepLabels}
            // onStepClick={(stepIndex) => {
            //   if (usedFor === "update") {
            //     setActiveStep(stepIndex)
            //   }
            // }}
            />
            <Box height={20} />

            {getStepContent(activeStep)}

            {usedFor === "update" && (
              <>
                <Box height={15} />
                <StyledDarkRedInformationTypography variant="body2">
                  Last Modified:
                  <StyledInformationSpan
                    style={{
                      color: "#2D2D37",
                      fontWeight: "bold",
                      marginLeft: "5px",
                      marginTop: "3px",
                    }}
                  >
                    {loopinfo.last_edit_by}
                  </StyledInformationSpan>
                </StyledDarkRedInformationTypography>
              </>
            )}

          </StyledMainBox>
        </DialogContent>
        <Box height={20} />
        <DialogActions>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            width="100%"
          >
            <CloseButton
              onClick={(e) => {
                e.preventDefault();
                if (typeof currentStateData.new_loop_data !== "undefined" && Object.keys(currentStateData.new_loop_data).length > 0) {
                  setShowCancelConfirmationConfirmation(true);
                } else {
                  closeModal();
                }
              }}
              label="Cancel"
              icon={<Close />}
            />

            {/* Right side - Navigation buttons */}
            <Stack direction="row" spacing={2} alignItems="flex-end">
              {
                activeStep >= 1 && (
                  <DarkGreyButton
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveStep((prev) => prev - 1);
                    }}
                    label="Back"
                    icon={<ArrowBack />}
                  />
                )
              }

              <ActionButton
                onClick={(e) => {
                  e.preventDefault();
                  // validate the current step before proceeding
                  const validate = validateCurrentStep();
                  if (validate.error_found === false) {
                    setFieldErrors({});
                    if (activeStep === 3) {
                      saveLoopCampaign(usedFor, acceptedFiles).then(info => {
                        if (info.success === true) {
                          // reset filters and load pictures list again
                          resetLoopsData();
                          closeModal();
                        } else {
                          // redirect to store list (if there's error while saving/updating)
                          setActiveStep(2);
                        }
                      })
                    } else {
                      setActiveStep((prev) => prev + 1);
                    }
                  }
                }}
                label={activeStep === 3 ? "Save" : "Next"}
                icon={activeStep === 3 ? <Save /> : <ArrowForward />}
              />
            </Stack>
          </Stack>
          {/* <Stack direction={"row"} spacing={2}>
          </Stack> */}
        </DialogActions>
        {/* ------- cancel confirmation -------- */}
        {showCancelConfirmation === true && (
          <Confirmation
            showConfirmationPopup={showCancelConfirmation}
            closePopup={(e) => {
              setShowCancelConfirmationConfirmation(false);
            }}
            confirmationActionCall={() => {
              closeModal();
            }}
            confirmationMessage={
              <>
                <Typography variant="body2">
                  Are you certain you want to discard all the changes you have made?
                </Typography>
              </>
            }
            confirmationTitle={"Confirmation"}
          />
        )}
      </Dialog>

    </>
  );
};

export default AddLoopCampaign;
